import React from 'react';
import { Accounts } from '../../components/atoms/Icons/Accounts';
import { Savings } from '../../components/atoms/Icons/Savings';
import { Card } from '../../components/atoms/Icons/Card';
import { Financial } from '../../components/atoms/Icons/Financial';
import { Loan } from '../../components/atoms/Icons/Loan';
import { Home } from '../../components/atoms/Icons/Home';
import { Profile } from '../../components/atoms/Icons/Profile';

export enum Capability {
  CHECKING_ACCOUNTS = 'CHECKING_ACCOUNTS',
  CREDIT_CARDS = 'CREDIT_CARDS',
  INVESTMENTS = 'INVESTMENTS',
  LOANS = 'LOANS',
  MORTGAGE_AGGREGATION = 'MORTGAGE_AGGREGATION',
  PAYMENTS = 'PAYMENTS',
  SAVINGS_ACCOUNTS = 'SAVINGS_ACCOUNTS',
  TRANSFERS = 'TRANSFERS',
  IDENTITY_DATA = 'IDENTITY_DATA',
}

export enum PISCapability {
  PIS_SE_BG = 'PIS_SE_BG',
  PIS_SE_PG = 'PIS_SE_PG',
  PIS_SE_BANK_TRANSFERS = 'PIS_SE_BANK_TRANSFERS',
  PIS_FUTURE_DATE = 'PIS_FUTURE_DATE',
  PIS_BULK_PAYMENTS = 'PIS_BULK_PAYMENTS',
  SEPA_CREDIT_TRANSFER = 'SEPA_CREDIT_TRANSFER',
  SEPA_INSTANT_CREDIT_TRANSFER = 'SEPA_INSTANT_CREDIT_TRANSFER',
  FASTER_PAYMENTS = 'FASTER_PAYMENTS',
  NORWEGIAN_DOMESTIC_CREDIT_TRANSFER = 'NORWEGIAN_DOMESTIC_CREDIT_TRANSFER',
  INSTANT_NORWEGIAN_DOMESTIC_CREDIT_TRANSFER_STRAKS = 'INSTANT_NORWEGIAN_DOMESTIC_CREDIT_TRANSFER_STRAKS',
  MULTIBANCO = 'MULTIBANCO',
  POLISH_DOMESTIC_CREDIT_TRANSFER = 'POLISH_DOMESTIC_CREDIT_TRANSFER',
  INSTANT_POLISH_DOMESTIC_CREDIT_TRANSFER = 'INSTANT_POLISH_DOMESTIC_CREDIT_TRANSFER',
  DANISH_DOMESTIC_CREDIT_TRANSFER = 'DANISH_DOMESTIC_CREDIT_TRANSFER',
  INSTANT_DANISH_DOMESTIC_CREDIT_TRANSFER_STRAKS = 'INSTANT_DANISH_DOMESTIC_CREDIT_TRANSFER_STRAKS',
}

export type AccessType =
  | 'ACCESS_TYPE_OTHER'
  | 'ACCESS_TYPE_OPEN_BANKING'
  | 'OPEN_BANKING'
  | 'OTHER';

export enum FinancialServiceSegment {
  FINANCIAL_SERVICE_SEGMENT_UNKNOWN = 'FINANCIAL_SERVICE_SEGMENT_UNKNOWN',
  FINANCIAL_SERVICE_SEGMENT_BUSINESS = 'FINANCIAL_SERVICE_SEGMENT_BUSINESS',
  FINANCIAL_SERVICE_SEGMENT_PERSONAL = 'FINANCIAL_SERVICE_SEGMENT_PERSONAL',
}

export type FinancialService = {
  segment: FinancialServiceSegment;
  shortName: string;
};

export enum AuthenticationUserType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

export type Provider = {
  /**
   * Setting authenticationUserType as optional since it has been deprecated
   * in favor of financialServices - which is not yet populated for all providers.
   * Currently consumed by TPP ProviderSelector until we can use finacianlServices.
   * */
  authenticationUserType?: AuthenticationUserType;
  accessType: AccessType;
  capabilities: Capability[];
  pisCapabilities: PISCapability[];
  displayName: string;
  financialInstitutionId: string;
  financialInstitutionName: string;
  groupDisplayName?: string;
  name: string;
  type: ProviderType;
  status: ProviderStatus;
  market?: string;
  financialServices?: FinancialService[];
};

export enum ProviderType {
  BANK = 'BANK',
  CREDIT_CARD = 'CREDIT_CARD',
  BROKER = 'BROKER',
  TEST = 'TEST',
  FRAUD = 'FRAUD',
}

export enum ProviderStatus {
  ENABLED = 'ENABLED',
  TEMPORARY_DISABLED = 'TEMPORARY_DISABLED',
  DISABLED = 'DISABLED',
  OBSOLETE = 'OBSOLETE',
}

export type Market = {
  countryCode: string;
  name: string;
};

export type ProviderGroup = {
  providers: Provider[];
  capabilities: Capability[];
  pisCapabilities: PISCapability[];
  displayName: string;
};

export type ProviderGroups = Map<string, ProviderGroup>;

export type CapabilityOption = {
  // Message key that you need to translate.
  label: string;
  value: Capability;
  icon?: JSX.Element;
};

export enum PisGroup {
  ONETIME = 'oneTime',
  FUTURE = 'future',
}

export type PisCapabilityOption = {
  // Message key that you need to translate.
  label: string;
  value: PisGroup;
  capabilities: PISCapability[];
  showList: boolean;
};

export const capabilityOptions: CapabilityOption[] = [
  {
    label: 'capability.checkingAccounts',
    value: Capability.CHECKING_ACCOUNTS,
    icon: <Accounts />,
  },
  {
    label: 'capability.savingsAccounts',
    value: Capability.SAVINGS_ACCOUNTS,
    icon: <Savings />,
  },
  {
    label: 'capability.creditCards',
    value: Capability.CREDIT_CARDS,
    icon: <Card />,
  },
  {
    label: 'capability.investments',
    value: Capability.INVESTMENTS,
    icon: <Financial />,
  },
  {
    label: 'capability.loans',
    value: Capability.LOANS,
    icon: <Loan />,
  },
  {
    label: 'capability.mortgages',
    value: Capability.MORTGAGE_AGGREGATION,
    icon: <Home />,
  },
  {
    label: 'capability.identity',
    value: Capability.IDENTITY_DATA,
    icon: <Profile />,
  },
];

export const pisCapabilityOptions: PisCapabilityOption[] = [
  {
    label: 'payments.table.paymentMethod',
    value: PisGroup.ONETIME,
    capabilities: [
      PISCapability.PIS_SE_BG,
      PISCapability.PIS_SE_PG,
      PISCapability.PIS_SE_BANK_TRANSFERS,
      PISCapability.SEPA_CREDIT_TRANSFER,
      PISCapability.SEPA_INSTANT_CREDIT_TRANSFER,
      PISCapability.FASTER_PAYMENTS,
      PISCapability.INSTANT_NORWEGIAN_DOMESTIC_CREDIT_TRANSFER_STRAKS,
      PISCapability.NORWEGIAN_DOMESTIC_CREDIT_TRANSFER,
      PISCapability.MULTIBANCO,
      PISCapability.POLISH_DOMESTIC_CREDIT_TRANSFER,
      PISCapability.INSTANT_POLISH_DOMESTIC_CREDIT_TRANSFER,
      PISCapability.DANISH_DOMESTIC_CREDIT_TRANSFER,
      PISCapability.INSTANT_DANISH_DOMESTIC_CREDIT_TRANSFER_STRAKS,
    ],
    showList: true,
  },
  {
    label: 'payments.table.futureDated',
    value: PisGroup.FUTURE,
    capabilities: [PISCapability.PIS_FUTURE_DATE],
    showList: false,
  },
];
